import api from './api';
import type { AreaPresetType } from './areas';

export interface Preset {
  id: string;
  areaId: string;
  name: string;
  type: AreaPresetType;
  address: number | null;
  userGroupId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PresetGroup {
  groupId: string;
  groupName: string;
  /** `light_preset_groups.id` for light preset ↔ group relationship (null when not linked). */
  lightPresetGroupId?: string | null;
  alias?: string | null;
  /** When true, group-level weekly windows override the preset’s schedule for this link. */
  overridePresetAvailableTimes?: boolean;
}

export interface AvailabilityTime {
  hasWindows: boolean;
  isWithinWindow: boolean;
  windowStartAt: string;
  windowEndAt: string;
}

export interface PresetResponse extends Preset {
  availability: AvailabilityTime;
  dashboardName?: string;
  groups: PresetGroup[];
  userGroupName?: string | null;
}

export interface CreatePresetData {
  areaId: string;
  name: string;
  address?: number; // Device address: 0-255
  type?: AreaPresetType;
  userGroupId?: string;
  groupIds?: string[];
}

export interface UpdatePresetData {
  name?: string;
  address?: number; // Device address: 0-255
  type?: AreaPresetType;
  userGroupId?: string | null;
  groupIds?: string[];
}

export interface PresetFilter {
  includeDeleted?: boolean;
  areaId?: string;
  address?: number;
  name?: string;
  availableOnly?: boolean;
  type?: AreaPresetType;
}

export interface PresetAvailableTimeRow {
  id: string;
  weekday: number;
  startTime: string;
  endTime: string;
  sortOrder: number;
}

export interface PresetAvailableTimesPayload {
  windows: PresetAvailableTimeRow[];
}

/** Response body `code` when PUT rejects overlapping curfew without confirmation. */
export const PRESET_AVAILABLE_TIMES_CURFEW_OVERLAP_CODE = 'CURFEW_OVERLAP' as const;

export const presetsApi = {
  getByArea: async (areaId: string): Promise<PresetResponse[]> => {
    const { data } = await api.get('/presets', { params: { areaId } });
    return data;
  },

  getWithFilter: async (filter: PresetFilter): Promise<PresetResponse[]> => {
    const { data } = await api.get('/presets', { params: filter });
    return data;
  },

  getById: async (id: string): Promise<PresetResponse> => {
    const { data } = await api.get(`/presets/${id}`);
    return data;
  },

  create: async (payload: CreatePresetData): Promise<PresetResponse> => {
    const { data } = await api.post('/presets', payload);
    return data;
  },

  update: async (id: string, payload: UpdatePresetData): Promise<PresetResponse> => {
    const { data } = await api.put(`/presets/${id}`, payload);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/presets/${id}`);
  },

  getAvailableTimes: async (presetId: string): Promise<PresetAvailableTimeRow[]> => {
    const { data } = await api.get<PresetAvailableTimesPayload>(`/presets/${presetId}/available-times`);
    return data.windows ?? [];
  },

  putAvailableTimes: async (
    presetId: string,
    windows: { weekday: number; startTime: string; endTime: string }[],
    options?: { confirmCurfewOverlap?: boolean },
  ): Promise<PresetAvailableTimeRow[]> => {
    const { data } = await api.put<PresetAvailableTimesPayload>(
      `/presets/${presetId}/available-times`,
      {
        windows,
        ...(options?.confirmCurfewOverlap ? { confirmCurfewOverlap: true } : {}),
      },
    );
    return data.windows ?? [];
  },
};
